import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { FilePenLine, Plus, Search } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import type { PageFull } from "@/types/page";

interface PagesListProps {
  pages: PageFull[];
  total: number;
  limit: number;
  page: number;
  search: string;
  loading?: boolean;
  onPageChange: (nextPage: number) => void;
  onSearchChange: (value: string) => void;
  onEdit: (page: PageFull) => void;
  onCreateNew: () => void;
  selectedPageId?: string | null;
}

const formatDate = (input: string) => {
  try {
    return format(new Date(input), "dd MMM yyyy · HH'h'mm", { locale: ptBR });
  } catch (error) {
    return "-";
  }
};

export function PagesList({
  pages,
  total,
  limit,
  page,
  search,
  loading,
  onPageChange,
  onSearchChange,
  onEdit,
  onCreateNew,
  selectedPageId,
}: PagesListProps) {
  const totalPages = Math.max(Math.ceil(total / limit), 1);

  return (
    <Card className="rounded-2xl border border-slate-200 bg-white/70 text-slate-900 backdrop-blur">
      <CardHeader className="space-y-4 pb-0">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <CardTitle className="text-xl font-semibold text-slate-900">Páginas cadastradas</CardTitle>
            <CardDescription className="text-sm text-slate-500">
              Monitore as rotas do site e mantenha suas informações de SEO em dia.
            </CardDescription>
          </div>
          <Button onClick={onCreateNew} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" /> Nova página
          </Button>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="w-full max-w-xl">
            <label className="block text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
              Busca por rota ou nome
            </label>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={event => onSearchChange(event.target.value)}
                placeholder="/sobre, Home, FAQ..."
                className="h-11 w-full rounded-xl border-slate-200 bg-white pl-10 text-sm text-slate-900 placeholder:text-slate-400"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
              {total} rotas
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-sm text-slate-500">
            Carregando páginas...
          </div>
        ) : pages.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white/80 p-8 text-center text-sm text-slate-500">
            Nenhuma página encontrada para o critério atual.
          </div>
        ) : (
          <div className="space-y-4">
            <div className="hidden md:block">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow className="text-xs uppercase tracking-wide text-slate-500">
                    <TableHead className="w-[26%] text-slate-500">Rota</TableHead>
                    <TableHead className="w-[22%] text-slate-500">Nome</TableHead>
                    <TableHead className="w-[16%] text-slate-500">Publicado</TableHead>
                    <TableHead className="w-[24%] text-slate-500">Atualizado em</TableHead>
                    <TableHead className="w-[12%] text-right text-slate-500">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pages.map(pageItem => {
                    const isActive = selectedPageId === pageItem.id;
                    return (
                      <TableRow
                        key={pageItem.id}
                        className="cursor-pointer rounded-xl text-sm text-slate-700 transition-colors hover:bg-slate-50/80"
                        onClick={() => onEdit(pageItem)}
                      >
                        <TableCell className="max-w-[220px] break-words text-pretty font-medium [overflow-wrap:anywhere]">
                          {pageItem.route}
                        </TableCell>
                        <TableCell className="text-pretty font-medium text-slate-600 [overflow-wrap:anywhere]">
                          {pageItem.name}
                        </TableCell>
                        <TableCell>
                          {pageItem.is_published ? (
                            <Badge className="bg-emerald-50 text-emerald-700" variant="outline">
                              Publicada
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-50 text-amber-700" variant="outline">
                              Rascunho
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">{formatDate(pageItem.updated_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant={isActive ? "default" : "outline"}
                            size="sm"
                            className="inline-flex items-center gap-2 rounded-lg border-slate-200"
                            onClick={event => {
                              event.stopPropagation();
                              onEdit(pageItem);
                            }}
                          >
                            <FilePenLine className="h-4 w-4" />
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-col gap-4 md:hidden">
              {pages.map(pageItem => (
                <button
                  key={pageItem.id}
                  type="button"
                  onClick={() => onEdit(pageItem)}
                  className="rounded-2xl border border-slate-200 bg-white/80 p-4 text-left shadow-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">
                      {pageItem.route}
                    </div>
                    <Badge
                      className={pageItem.is_published ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}
                      variant="outline"
                    >
                      {pageItem.is_published ? "Publicada" : "Rascunho"}
                    </Badge>
                  </div>
                  <p className="mt-2 text-lg font-semibold text-slate-900">{pageItem.name}</p>
                  <p className="text-xs text-slate-500">Atualizado em {formatDate(pageItem.updated_at)}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm text-slate-600 sm:flex-row">
          <span className="text-xs">
            Página {page} de {totalPages}
          </span>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                className="w-full border-slate-200 text-slate-700 hover:border-[#FF5605] hover:bg-[#FF5605]/10 sm:w-auto"
                onClick={() => onPageChange(Math.max(1, page - 1))}
                disabled={page <= 1 || loading}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                className="w-full border-slate-200 text-slate-700 hover:border-[#FF5605] hover:bg-[#FF5605]/10 sm:w-auto"
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages || loading}
              >
                Próxima
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
